import { useState } from "react";
import { useLocation } from "@/lib/router-compat";
import { X } from "lucide-react";
import { useDeferredMount } from "@/hooks/useDeferredMount";

const DEFAULT_WHATSAPP_NUMBER = "97333606055";
const CAREERS_WHATSAPP_NUMBER = "97333604477";
const DEFAULT_MESSAGE = "Hello";
const CAREERS_MESSAGE =
  "Hello, I'm interested in a career opportunity at Abacus Interior Decoration.";
const DEFAULT_GREETING =
  "Hello! How can we help you with your interior design or fit-out project?";
const CAREERS_GREETING =
  "Hello! We'd love to hear from you about career opportunities at Abacus Interior Decoration.";

const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <path
      fill="currentColor"
      d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"
    />
  </svg>
);

export default function WhatsAppWidget() {
  const ready = useDeferredMount();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isCareers = location.pathname.startsWith("/careers");
  const number = isCareers ? CAREERS_WHATSAPP_NUMBER : DEFAULT_WHATSAPP_NUMBER;
  const message = isCareers ? CAREERS_MESSAGE : DEFAULT_MESSAGE;
  const greeting = isCareers ? CAREERS_GREETING : DEFAULT_GREETING;

  const link = `https://wa.me/${number}?text=${encodeURIComponent(message)}`;

  // Defer the entire widget until after main content is interactive so the
  // floating button and its assets never block the first contentful paint.
  if (!ready) return null;

  return (
    <div className="fixed bottom-5 right-5 z-50 flex flex-col items-end gap-3">
      {open && (
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 w-72 overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-200">
          <div className="bg-[#075E54] p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <WhatsAppIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-white font-semibold text-sm">
                  {isCareers
                    ? "Abacus Careers / HR"
                    : "Abacus Design Solutions"}
                </p>
                <p className="text-white/70 text-xs">
                  {isCareers
                    ? "Typically replies within a day"
                    : "Typically replies within an hour"}
                </p>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-white/70 hover:text-white transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="p-4 bg-[#ECE5DD]">
            <div className="bg-white rounded-lg p-3 text-sm text-gray-700 shadow-xs mb-4 max-w-[85%]">
              {greeting}
            </div>
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full bg-[#25D366] text-white font-semibold text-sm py-3 rounded-lg flex items-center justify-center gap-2 hover:bg-[#1ebe5d] transition-colors"
            >
              <WhatsAppIcon className="w-5 h-5 text-white" />
              Start Chat
            </a>
          </div>
        </div>
      )}
      <button
        onClick={() => setOpen(!open)}
        className="w-14 h-14 rounded-full bg-[#25D366] shadow-lg flex items-center justify-center hover:bg-[#1ebe5d] transition-all hover:scale-110 active:scale-95"
        aria-label="Open WhatsApp chat"
      >
        {open ? (
          <X className="w-7 h-7 text-white" />
        ) : (
          <WhatsAppIcon className="w-8 h-8 text-white" />
        )}
      </button>
    </div>
  );
}
